// Shared AMA (Annual Maintenance Agreement, subscriptionId 1) subscription chain.
//
// Runs the full chain used after an AMA payment / before CreateUserRecurringSubscription:
//   0. getUserSubscription            -> check if a subscription already exists
//   1. add (new) OR update (existing) -> yields userSubscriptionId; sets orderId, dates, amaFromAR
//   2. Subscription/Details           -> master SKU list
//   3. UserSubscriptionPlans/upsert   -> plan for the SKU whose endSubscriptionAmt == amount
//   4. getUserSubscription            -> confirm
//   5. GetUserSubscriptionPlansAsync  -> the (now created) plans
//
// AMA-only: if lineOfBusiness is not 1, the whole chain is skipped (no API is hit).
// Non-blocking: any failure is logged and swallowed so the caller's flow is unaffected.

import konsole from "./konsole";
// NOTE: Services is passed in as a parameter (not imported) to avoid a circular
// import between Services.js and this module (which caused a TDZ ReferenceError:
// "Cannot access 'Services' before initialization").

const toNumber = (v) => {
  if (v == null) return null;
  const n = parseFloat(String(v).replace(/,/g, ""));
  return isNaN(n) ? null : n;
};

// Converts a date to MM-DD-YYYY. Accepts YYYY-MM-DD, MM-DD-YYYY, or a Date-parseable string.
const toMMDDYYYY = (d) => {
  if (d == null || d === "") {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    return `${mm}-${dd}-${now.getFullYear()}`;
  }
  const parts = String(d).split("T")[0].split("-");
  if (parts?.length === 3) {
    // YYYY-MM-DD -> MM-DD-YYYY ; MM-DD-YYYY stays as-is
    if (parts[0]?.length === 4) return `${parts[1]}-${parts[2]}-${parts[0]}`;
    return `${parts[0]}-${parts[1]}-${parts[2]}`;
  }
  return String(d);
};

const addOneYear = (mmddyyyy) => {
  const parts = String(mmddyyyy).split("-");
  if (parts?.length !== 3) return mmddyyyy;
  const [mm, dd, yyyy] = parts;
  return `${mm}-${dd}-${String(Number(yyyy) + 1)}`;
};

// Formats a Date back to MM-DD-YYYY.
const dateToMMDDYYYY = (d) => {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${mm}-${dd}-${d.getFullYear()}`;
};

// Next installment date = installment date + one frequency step.
// installmentFrequencies: 7 = Weekly (add 7 days), 1 = Monthly, 3 = Quarterly,
// 6 = Half-yearly, 12 = Yearly (add that many months).
const nextInstallmentDate = (mmddyyyy, installmentFrequencies) => {
  const parts = String(mmddyyyy).split("-");
  if (parts?.length !== 3) return mmddyyyy;
  const [mm, dd, yyyy] = parts?.map((p) => Number(p));
  const base = new Date(yyyy, mm - 1, dd);
  const freq = Number(installmentFrequencies);
  if (freq === 7) {
    base.setDate(base.getDate() + 7); // Weekly
  } else {
    // Monthly(1), Quarterly(3), Half-yearly(6), Yearly(12) -> add that many months.
    const months = isNaN(freq) ? 1 : freq;
    base.setMonth(base.getMonth() + months);
  }
  return dateToMMDDYYYY(base);
};

const isValid = (v) => v !== null && v !== undefined && v !== "";

// Picks the SKU whose endSubscriptionAmt equals the payment amount.
const pickSkuByAmount = (detailsRes, amount) => {
  const amt = toNumber(amount);
  const detail = detailsRes?.data?.data?.[0];
  const allSkus = (detail?.subsPlans || [])?.flatMap((pl) => pl?.subsPlanSKUs || []);
  if (!allSkus?.length || amt == null) return null;
  return allSkus?.find((s) => Number(s?.endSubscriptionAmt) === amt) || null;
};

// Builds the add/update payload from the provided fields.
const buildPayload = ({ subtenantId, userId, orderId, amount, paid, responsibleAttorney, originatingAttorney, loggedUserId, paymentDate, isAma, isInstallment, installmentFrequencies, futurePayDateNull }) => {
  const payDate = toMMDDYYYY(paymentDate);
  // Non-installment: futurePayDate = validityFrom + 1 year.
  // Installment:     validityFrom = installment date; futurePayDate = SAME installment
  //                  date (the first/current installment is not paid yet, so the next
  //                  due date is this installment date itself).
  // futurePayDate is always null now.
  const futurePayDate = null;
  // validityTo (a date):
  //  - Installment: up to the NEXT installment date (validityFrom + one frequency step).
  //  - Non-installment: validityFrom + 1 year.
  const validityTo = isInstallment
    ? nextInstallmentDate(payDate, installmentFrequencies)
    : addOneYear(payDate);
  return {
    userId,
    attorneyId: responsibleAttorney || null,          // Responsible Attorney
    originatingAttorneyId: originatingAttorney || null, // Originating Staff
    clientAcquisitionManagerId: null,
    isActive: true,
    subsStatusId: 1,
    subscriptionId: isAma ? "1" : null, // "1" for AMA, else null
    amaFromAR: isAma ? true : false,    // true when paying for AMA
    subtenantId,
    orderId: orderId ?? null,
    validityFrom: payDate,
    futurePayDate,
    createdBy: loggedUserId || null,
    totalAmount: amount,
    totalPaid: paid,
    validityTo,
    missedRenewals: null,
    modifidyFeeStruture: null,
  };
};

/**
 * Runs the AMA subscription chain. All inputs are plain values (no component state).
 * Safe to call from any flow (Dashboard receipt, card-save/recurring, etc.).
 */
export const runAmaSubscriptionChain = async ({
  Services,          // the Services object, passed in by the caller (avoids circular import)
  subtenantId,
  userId,
  orderId,
  amount,            // total amount
  payAmount,         // paid amount (falls back to amount)
  responsibleAttorney,
  originatingAttorney,
  lineOfBusiness,    // service type; AMA = 1
  loggedUserId,
  paymentDate,       // used for validityFrom / futurePayDate / modifidyFeeStruture (non-installment)
  // Installment fields (present only for the installment flow):
  installmentDate,        // selected installment date -> validityFrom
  installmentAmount,      // per-installment amount -> totalAmount/totalPaid
  installmentFrequencies, // 7=Weekly(days), 1=Monthly, 3=Quarterly, 6=Half-yearly, 12=Yearly (months)
  futurePayDateNull,      // when true (cash/check/ACH installment), send futurePayDate = null
}) => {
  try {
    if (!Services) { konsole.log("runAmaSubscriptionChain: Services not provided"); return; }
    if (!isValid(userId)) return;
    // AMA-only guard: skip entirely for any other service type (no API hit).
    if (Number(lineOfBusiness) !== 1) {
      konsole.log("runAmaSubscriptionChain skipped: service type is not AMA (lineOfBusiness=", lineOfBusiness, ")");
      return;
    }

    // Installment mode: when an installment date is provided, use the installment
    // date + futurePayDate = next installment date. totalAmount/totalPaid use the
    // TOTAL amount (not the per-installment amount).
    const isInstallment = isValid(installmentDate);
    const effectivePaymentDate = isInstallment ? installmentDate : paymentDate;
    const totalNum = toNumber(amount);
    // totalAmount/totalPaid use the TOTAL amount. In installment mode both are the total
    // (the per-installment amount is NOT used here). Non-installment falls back to payAmount.
    const amountNum = totalNum ?? toNumber(payAmount);
    const paidNum = isInstallment ? (totalNum ?? toNumber(payAmount)) : (toNumber(payAmount) ?? totalNum);
    // SKU is matched against the TOTAL amount (endSubscriptionAmt).
    const skuMatchAmount = totalNum ?? amountNum;

    // Step 0: existing subscription check.
    const existingRes = await Services.getUserSubscription(subtenantId, 1, userId);
    const existing = existingRes?.data?.data;
    const existingUserSubscriptionId = Array.isArray(existing) ? existing?.[0]?.userSubscriptionId : null;

    const payload = buildPayload({
      subtenantId, userId, orderId,
      amount: amountNum, paid: paidNum,
      responsibleAttorney, originatingAttorney,
      loggedUserId,
      paymentDate: effectivePaymentDate,
      isAma: true,
      isInstallment,
      installmentFrequencies,
      futurePayDateNull,
    });
    konsole.log("runAmaSubscriptionChain payload (orderId:", orderId, "):", payload);

    // Step 1: add or update.
    let userSubscriptionId = existingUserSubscriptionId ?? null;
    if (isValid(existingUserSubscriptionId)) {
      await Services.updateUserSubscription({ ...payload, userSubscriptionId: existingUserSubscriptionId, updatedBy: loggedUserId || null });
    } else {
      const addRes = await Services.addUserSubscription(payload);
      userSubscriptionId = addRes?.data?.data ?? null;
      // The add endpoint does not persist orderId, so update right after to store it.
      if (isValid(userSubscriptionId) && isValid(orderId)) {
        await Services.updateUserSubscription({ ...payload, userSubscriptionId, updatedBy: loggedUserId || null });
      }
    }

    // Step 2+3: Subscription/Details -> pick SKU by amount -> upsert plan.
    if (isValid(userSubscriptionId)) {
      const detailsRes = await Services.getSubscriptionDetails(subtenantId, 1);
      const sku = pickSkuByAmount(detailsRes, skuMatchAmount);
      konsole.log("runAmaSubscriptionChain matched SKU for amount", skuMatchAmount, ":", sku);
      if (sku) {
        const upsertPayload = [{
          userSubscriptionId,
          userSubsPlanId: null,
          subsPlanSKUMapId: sku?.subsPlanSKUMapId,
          isActive: true,
          upsertedBy: loggedUserId || null,
          subtenantRateCardId: sku?.subtenantRateCardId,
        }];
        konsole.log("runAmaSubscriptionChain upsert payload:", JSON.stringify(upsertPayload));
        const upsertRes = await Services.upsertUserSubscriptionPlans(upsertPayload);
        konsole.log("runAmaSubscriptionChain upsert response:", upsertRes);
      } else {
        konsole.log("runAmaSubscriptionChain: no SKU matched total amount", skuMatchAmount, "- upsert skipped");
      }
      // Step 4 + 5: confirm + fetch the created plans.
      await Services.getUserSubscription(subtenantId, 1, userId);
      const plansRes = await Services.getUserSubscriptionPlansAsync(userSubscriptionId, true);
      // Prefer the userSubscriptionId from GetUserSubscriptionPlansAsync (the created plan),
      // falling back to the one from add. This is what the recurring API should receive.
      const planUserSubscriptionId = plansRes?.data?.data?.[0]?.userSubscriptionId;
      konsole.log("runAmaSubscriptionChain plan userSubscriptionId:", planUserSubscriptionId, "add userSubscriptionId:", userSubscriptionId);
      if (isValid(planUserSubscriptionId)) {
        userSubscriptionId = planUserSubscriptionId;
      }
    }
    // Return the userSubscriptionId (preferably from GetUserSubscriptionPlansAsync) so the
    // caller can pass it to the recurring API (CreateUserRecurringSubscription) — the
    // backend needs it to link the authorize ids back onto this subscription.
    return userSubscriptionId;
  } catch (e) {
    konsole.log("runAmaSubscriptionChain failed:", e);
    return null;
  }
};

/**
 * After CreateUserRecurringSubscription succeeds, the BACKEND itself saves the authorize
 * profile ids (authorizeCustomerProfileId / authorizePaymentProfileId /
 * authorizeSubscriptionId) onto the AMA subscription. So we do NOT update anything here —
 * we just call getUserSubscription once so the refreshed record (with the ids) is fetched.
 *
 * (A previous version PUT /update with these ids, but that endpoint does not persist them
 * and was overwriting them to null, so the update was removed.)
 *
 * @param {object} args
 * @param {object} args.Services        the Services object (passed in; avoids circular import)
 * @param {number|string} args.subtenantId
 * @param {string} args.userId
 */
export const updateAmaSubscriptionAfterRecurring = async ({ Services, subtenantId, userId }) => {
  try {
    if (!Services) { konsole.log("updateAmaSubscriptionAfterRecurring: Services not provided"); return; }
    if (!isValid(userId)) return;
    // Backend persists the authorize ids on its own; just fetch the refreshed subscription.
    const refreshedRes = await Services.getUserSubscription(subtenantId, 1, userId);
    konsole.log("post-recurring getUserSubscription:", JSON.stringify(refreshedRes?.data?.data?.[0]));
    return refreshedRes;
  } catch (e) {
    konsole.log("updateAmaSubscriptionAfterRecurring failed:", e);
  }
};
